//DailyHelpPage.js
import React, { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import DashboardLayout from '../components/DashboardLayout';
import { dailyHelpService } from '../Services/dailyHelpService';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import {
  Home,
  Search,
  MessageSquare,
  ClipboardList,
  Shield,
  DollarSign,
  Clock,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Send,
  Baby,
  Puzzle,
  BookOpen,
  Bot,
  Star,
  Users,
  Heart,
  User as UserIcon,
  Loader2,
  ArrowLeft,
  ExternalLink,
} from 'lucide-react';

import SEO from '../components/SEO';

// import ReactMarkdown from "react-markdown"; // Inutilisé dans ce fichier si on formatte en HTML simple

const DailyHelpPage = () => {
  const [view, setView] = useState('home');
  const navigate = useNavigate();

  // Articles
  const [articlesByCategory, setArticlesByCategory] = useState({});
  const [loadingArticles, setLoadingArticles] = useState(true);
  const [showAllArticles, setShowAllArticles] = useState(false);

  // ⭐ NOUVEAU : État pour le filtre par âge
  const ageFilters = ['Tous', '0-2 ans', '3-5 ans', '6-12 ans', '13-18 ans', '18 ans +', 'Senior'];
  const [selectedAge, setSelectedAge] = useState('Tous');

  // Chat
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);

  // Recherche
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // CATEGORIES (Conservées pour garder les icônes et couleurs des badges sur les cartes)
  const categories = [
    {
      id: 'petite-enfance',
      title: 'Petite Enfance',
      age: '0-2 ans',
      icon: Baby,
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-pink-500',
    },
    {
      id: 'enfance',
      title: 'Enfance',
      age: '3-5 ans',
      icon: Puzzle,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-500',
    },
    {
      id: 'preado',
      title: 'Préado',
      age: '6-12 ans',
      icon: BookOpen,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-500',
    },
    {
      id: 'adolescence',
      title: 'Adolescence',
      age: '13-18 ans',
      icon: Star,
      color: 'from-indigo-500 to-violet-500',
      bgColor: 'bg-indigo-500',
    },
    {
      id: 'adulte',
      title: '18 ans +',
      age: '18 ans +',
      icon: Users,
      color: 'from-red-500 to-orange-500',
      bgColor: 'bg-red-500',
    },
    {
      id: 'senior',
      title: 'Senior',
      age: 'Senior',
      icon: Heart,
      color: 'from-yellow-500 to-amber-500',
      bgColor: 'bg-yellow-500',
    },
  ];

  const getCategoryInfo = (article) => {
    const tags = article.tags || [];
    const tagStr = tags.join(' ').toLowerCase();

    if (tagStr.includes('0-2')) return categories[0];
    if (tagStr.includes('3-5')) return categories[1];
    if (tagStr.includes('6-12')) return categories[2];
    if (tagStr.includes('13-18')) return categories[3];
    if (tagStr.includes('18 ans') || tagStr.includes('adulte')) return categories[4];
    if (tagStr.includes('senior')) return categories[5];

    const categoryId = article.category || '';
    if (categoryId === 'sante-enfant' || categoryId === 'petite-enfance') return categories[0];
    if (categoryId === 'enfance') return categories[1];
    if (categoryId === 'preado') return categories[2];
    if (categoryId === 'adolescence') return categories[3];
    if (categoryId === 'adulte') return categories[4];
    if (categoryId === 'senior') return categories[5];
    return categories[1];
  };

  useEffect(() => {
    if (view === 'chat' && chatMessages.length === 0) {
      setChatMessages([
        {
          role: 'assistant',
          content: `Bonjour ! 🏠 Je vous accompagne pour améliorer votre quotidien à la maison.\n\nPosez votre question : aménagement, sécurité, organisation ou solutions pratiques.`,
          timestamp: new Date(),
        },
      ]);
    }
  }, [view, chatMessages.length]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [chatInput]);

  useEffect(() => {
    loadPublishedArticles();
  }, []);

  const loadPublishedArticles = async () => {
    try {
      setLoadingArticles(true);
      const articles = await dailyHelpService.getPublishedArticles();

      const grouped = {
        'sante-enfant': [],
        'sante-adulte': [],
        'proche-age': [],
      };

      articles.forEach((article) => {
        const category = article.category || 'sante-adulte';
        if (grouped[category]) {
          grouped[category].push(article);
        } else {
          grouped['sante-adulte'].push(article);
        }
      });

      setArticlesByCategory(grouped);
    } catch (error) {
      console.error('❌ Erreur:', error);
    } finally {
      setLoadingArticles(false);
    }
  };

  const handleChatSend = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');

    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
    };
    setChatMessages([...chatMessages, newUserMessage]);

    setChatLoading(true);

    try {
      const aiResponse = await dailyHelpService.askDailyHelp(userMessage, null, chatMessages);

      setChatMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: aiResponse,
          timestamp: new Date(),
        },
      ]);
    } catch (error) {
      console.error('Erreur chat:', error);
      setChatMessages((prev) => prev.slice(0, -1));
      toast.error("Erreur lors de l'envoi du message");
    } finally {
      setChatLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    const results = await dailyHelpService.searchResources(searchQuery);
    setSearchResults(results);
    setView('resources');
  };

  const formatMessage = (content) => {
    const formatted = content
      .replace(
        /\*\*(.*?)\*\*/g,
        '<strong class="font-semibold text-gray-900 dark:text-gray-100">$1</strong>'
      )
      .replace(/\n/g, '<br/>');
    return { __html: DOMPurify.sanitize(formatted, {
      ALLOWED_TAGS: ['p','b','i','ul','ol','li','br','strong','em','h2','h3','a'],
      ALLOWED_ATTR: ['href', 'target', 'rel']
    }) };
  };

  // Vue principale (Home)
  if (view === 'home') {
    // ⭐ LOGIQUE DE FILTRAGE PAR ÂGE
    const allArticles = Object.values(articlesByCategory).flat();

    const filteredArticles =
      selectedAge === 'Tous'
        ? allArticles
        : allArticles.filter((article) => {
            // On vérifie si un des tags de l'article correspond (en ignorant les majuscules/minuscules)
            return (
              article.tags &&
              article.tags.some(
                (tag) => tag.toLowerCase().trim() === selectedAge.toLowerCase().trim()
              )
            );
          });

    const articlesPerPage = 6;
    const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);
    const visibleArticles = filteredArticles.slice(
      currentPage * articlesPerPage,
      (currentPage + 1) * articlesPerPage
    );

    return (
      <DashboardLayout>
        {/* Ajout d'un petit style pour masquer la scrollbar de la barre de navigation tout en gardant le scroll */}
        <style>{`
          .hide-scrollbar::-webkit-scrollbar { display: none; }
          .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        `}</style>

        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                <Home className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100">
                  Aide au Quotidien & Maintien à Domicile
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Conseils, ressources et accompagnement pour mieux vivre au quotidien
                </p>
              </div>
            </div>

            {/* Barre de recherche */}
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Rechercher un conseil, une aide technique..."
                  className="w-full pl-12 pr-4 py-4 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:border-green-500 focus:ring-4 focus:ring-green-100 dark:focus:ring-green-900/30 transition-all text-gray-900 dark:text-gray-100 placeholder-gray-400"
                />
              </div>
              <Button
                onClick={handleSearch}
                className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl shadow-lg"
              >
                Rechercher
              </Button>
            </div>
          </div>

          {/* Actions rapides */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card
              className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30 border-2 border-blue-200 dark:border-blue-800 p-8 cursor-pointer hover:shadow-xl transition-all group hover:-translate-y-1"
              onClick={() => navigate('/evaluation')}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
                  <ClipboardList className="w-8 h-8 text-white" />
                </div>
                <ChevronRight className="w-6 h-6 text-blue-600 dark:text-blue-400 group-hover:translate-x-1 transition-transform" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Évaluer mes besoins
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Quiz personnalisé pour identifier vos priorités et obtenir un plan d'action
                sur-mesure
              </p>
              <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 font-medium">
                <Clock className="w-4 h-4" />5 minutes
              </div>
            </Card>

            <Card
              className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 border-2 border-purple-200 dark:border-purple-800 p-8 cursor-pointer hover:shadow-xl transition-all group hover:-translate-y-1"
              onClick={() => setView('chat')}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
                  <MessageSquare className="w-8 h-8 text-white" />
                </div>
                <ChevronRight className="w-6 h-6 text-purple-600 dark:text-purple-400 group-hover:translate-x-1 transition-transform" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Poser une question
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Discutez avec notre assistant IA spécialisé dans l'aide quotidienne
              </p>
              <div className="flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400 font-medium">
                <Bot className="w-4 h-4" />
                Réponse instantanée
              </div>
            </Card>
          </div>

          {/* ⭐ BARRE INDEX GLISSANTE */}
          <div className="mb-6 w-full overflow-x-auto hide-scrollbar pb-2">
            {/* L'astuce est ici : on utilise w-max et mx-auto */}
            <div className="flex gap-3 w-max mx-auto px-2">
              {ageFilters.map((age) => (
                <button
                  key={age}
                  onClick={() => {
                    setSelectedAge(age);
                    setShowAllArticles(false);
                  }}
                  className={`px-5 py-2.5 rounded-full border-2 text-sm md:text-base font-medium transition-all ${
                    selectedAge === age
                      ? 'bg-gradient-to-r from-pink-500 to-rose-500 border-transparent text-white shadow-md'
                      : 'bg-transparent border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-pink-400 hover:text-pink-500 dark:hover:text-pink-400'
                  }`}
                >
                   {age} 
                </button>
              ))}
            </div>
          </div>

          {/* Section Articles Filtrés */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wide">
                {selectedAge === 'Tous'
                  ? 'Tous les articles disponibles'
                  : `Sélection d'articles pour les ${selectedAge}`}
              </h2>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {filteredArticles.length} article{filteredArticles.length > 1 ? 's' : ''}
                </span>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                      disabled={currentPage === 0}
                      className="w-9 h-9 rounded-lg border-2 border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    >
                      <ChevronUp className="w-4 h-4 rotate-[-90deg] text-green-600 dark:text-green-400" />
                    </button>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400 min-w-[3rem] text-center">
                      {currentPage + 1} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                      disabled={currentPage >= totalPages - 1}
                      className="w-9 h-9 rounded-lg border-2 border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    >
                      <ChevronDown className="w-4 h-4 rotate-[-90deg] text-green-600 dark:text-green-400" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {loadingArticles ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              </div>
            ) : filteredArticles.length === 0 ? (
              <Card className="p-12 text-center bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 rounded-xl">
                <p className="text-gray-500 dark:text-gray-400">
                  Aucun article trouvé pour la tranche d'âge "{selectedAge}".
                </p>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-5">
                {visibleArticles.map((article) => {
                  const categoryInfo = getCategoryInfo(article);
                  const Icon = categoryInfo.icon;

                  return (
                    <Card
                      key={article.id}
                      className="relative bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 hover:shadow-lg transition-all cursor-pointer rounded-xl group overflow-hidden"
                      onClick={() => navigate(`/daily-help/article/${article.id}`)}
                    >
                      <div className="flex h-full">
                        <div className="flex-1 p-5 flex flex-col justify-between min-w-0">
                          <div>
                            <div
                              className={`inline-flex w-7 h-7 ${categoryInfo.bgColor} rounded-lg items-center justify-center shadow-md mb-2`}
                              title={categoryInfo.title}
                            >
                              <Icon className="w-3.5 h-3.5 text-white" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2 group-hover:text-pink-500 transition-colors line-clamp-2">
                              {article.title}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-3">
                              {article.description || article.excerpt}
                            </p>
                          </div>
                          <div>
                            <div className="flex flex-wrap gap-1 mb-2">
                              {article.tags?.slice(0, 3).map((tag, idx) => (
                                <span
                                  key={idx}
                                  className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-md"
                                >
                                  {tag}
                                </span>
                              ))}
                              {article.tags?.length > 3 && (
                                <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-400 px-2 py-0.5 rounded-md">
                                  +{article.tags.length - 3}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 group-hover:text-pink-500 transition-colors">
                              <ExternalLink className="w-3 h-3" />
                              Lire l'article
                            </div>
                          </div>
                        </div>
                        <div className="w-36 sm:w-44 flex-shrink-0">
                          {article.thumbnail || article.images?.[0] ? (
                            <img
                              src={article.thumbnail || article.images[0]}
                              alt={article.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 flex items-center justify-center">
                              <Icon className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Vues Resources et Chat inchangées
  if (view === 'resources') {
    // ... (Je garde ce code intact pour toi, il fonctionne déjà très bien)
    return (
      <DashboardLayout>
        <SEO
          title="Articles Santé"
          description="Conseils santé par tranche d'âge pour toute la famille."
          path="/daily-help"
        />

        <div className="max-w-6xl mx-auto px-4 py-6">
          <Button
            onClick={() => setView('home')}
            variant="outline"
            className="mb-6 flex items-center gap-2 dark:border-gray-700 dark:text-gray-300"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Button>

          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Résultats de recherche
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {searchResults.length} résultat(s) pour "{searchQuery}"
            </p>
          </div>

          {searchResults.length === 0 ? (
            <Card className="p-12 text-center bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800">
              <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Aucun résultat trouvé
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Essayez avec d'autres mots-clés ou posez votre question à notre assistant
              </p>
              <Button
                onClick={() => setView('chat')}
                className="mt-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                Parler à l'assistant
              </Button>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {searchResults.map((resource) => {
                const categoryInfo = getCategoryInfo(resource);
                const Icon = categoryInfo.icon;

                return (
                  <Card
                    key={resource.id}
                    className="relative bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 p-6 hover:shadow-lg transition-all"
                  >
                    <div
                      className={`absolute top-3 right-3 w-8 h-8 ${categoryInfo.bgColor} rounded-lg flex items-center justify-center shadow-md`}
                      title={categoryInfo.title}
                    >
                      <Icon className="w-4 h-4 text-white" />
                    </div>

                    <div className="flex items-start justify-between mb-3 pr-10">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                        {resource.title}
                      </h3>
                    </div>

                    {resource.difficulty && (
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-medium mb-3 ${
                          resource.difficulty === 'Facile'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            : resource.difficulty === 'Moyen'
                              ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                        }`}
                      >
                        {resource.difficulty}
                      </span>
                    )}

                    <p className="text-gray-700 dark:text-gray-300 mb-4 line-clamp-2">
                      {resource.description}
                    </p>

                    {resource.budget && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
                        <DollarSign className="w-4 h-4" />
                        {resource.budget}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 mb-4">
                      {resource.tags?.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <Button
                      variant="outline"
                      className="w-full dark:border-gray-700 dark:text-gray-300"
                      onClick={() => navigate(`/daily-help/article/${resource.id}`)}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Voir l'article
                    </Button>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </DashboardLayout>
    );
  }

  // Vue Chat Inchangée
  if (view === 'chat') {
    return (
      <DashboardLayout>
        {/* Ton code pour le chat reste 100% identique ici */}
        <div className="max-w-5xl mx-auto px-4 py-6">
          <Button
            onClick={() => setView('home')}
            variant="outline"
            className="mb-6 flex items-center gap-2 dark:border-gray-700 dark:text-gray-300"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Button>

          <Card className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 overflow-hidden">
            <div className="border-b-2 border-gray-200 dark:border-gray-800 p-6 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Bot className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    Assistant vie quotidienne
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Des conseils pratiques pour votre maison et votre quotidien.
                  </p>
                </div>
              </div>
            </div>

            <div
              className="p-6 space-y-4 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white dark:from-gray-900 dark:to-gray-950"
              style={{ height: 'calc(100vh - 400px)' }}
            >
              {chatMessages.map((message, index) => (
                <div
                  key={index}
                  className={`flex gap-3 ${
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  {message.role === 'assistant' && (
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Bot className="w-6 h-6 text-white" />
                    </div>
                  )}

                  <div className={`max-w-[75%] ${message.role === 'user' ? 'order-1' : ''}`}>
                    <div
                      className={`
                        rounded-2xl px-5 py-4 shadow-md
                        ${
                          message.role === 'user'
                            ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white'
                            : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-2 border-gray-100 dark:border-gray-700'
                        }
                      `}
                    >
                      <div
                        className={`prose prose-sm max-w-none ${message.role === 'user' ? 'prose-invert' : 'dark:prose-invert'}`}
                        dangerouslySetInnerHTML={formatMessage(message.content)}
                      />
                    </div>

                    <div
                      className={`text-xs text-gray-400 dark:text-gray-500 mt-1.5 px-2 ${message.role === 'user' ? 'text-right' : 'text-left'}`}
                    >
                      {message.timestamp?.toLocaleTimeString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  </div>

                  {message.role === 'user' && (
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-700 to-gray-900 dark:from-gray-600 dark:to-gray-800 flex items-center justify-center flex-shrink-0 shadow-lg order-2">
                      <UserIcon className="w-6 h-6 text-white" />
                    </div>
                  )}
                </div>
              ))}

              {chatLoading && (
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Bot className="w-6 h-6 text-white" />
                  </div>
                  <div className="bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 rounded-2xl px-5 py-4 shadow-md">
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1.5">
                        <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-bounce" />
                        <div
                          className="w-2.5 h-2.5 bg-green-500 rounded-full animate-bounce"
                          style={{ animationDelay: '150ms' }}
                        />
                        <div
                          className="w-2.5 h-2.5 bg-green-500 rounded-full animate-bounce"
                          style={{ animationDelay: '300ms' }}
                        />
                      </div>
                      <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                        Réflexion...
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="border-t-2 border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-900">
              <form onSubmit={handleChatSend} className="flex gap-3 items-end">
                <textarea
                  ref={textareaRef}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleChatSend(e);
                    }
                  }}
                  placeholder="Posez votre question..."
                  rows="1"
                  className="flex-1 px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:border-green-500 dark:focus:border-green-400 focus:ring-4 focus:ring-green-100 dark:focus:ring-green-900/30 resize-none transition-all text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  style={{ maxHeight: '120px', minHeight: '48px' }}
                  disabled={chatLoading}
                />
                <Button
                  type="submit"
                  disabled={!chatInput.trim() || chatLoading}
                  className="h-12 w-12 bg-gradient-to-br from-green-500 to-emerald-600 hover:opacity-90 text-white rounded-xl disabled:opacity-50 transition-all shadow-lg"
                >
                  <Send className="w-5 h-5" />
                </Button>
              </form>
            </div>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return null;
};

export default DailyHelpPage;
